import { useQuery } from "@tanstack/react-query";
import { getTokenIconUrl, getNetworkIconUrl } from "@sdk/utils/config";
import React, { useEffect } from "react";
import type { Network, PaymentMethod, Currency, Token } from "@sdk/shared/types/config.types";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { AD_QUERY_KEY, QUICK_TRADE_WAIT_TIME_KEY } from "@shared/src/lib/queryKeys";
import { useP2PConfig } from "../useP2PConfig";
import { base, celo } from "viem/chains";

interface OptionItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface QuickTradeOptions {
  networkOptions: OptionItem[];
  paymentOptions: OptionItem[];
  currencyOptions: OptionItem[];
  tokenOptions: OptionItem[];
  getTokensForNetwork: (networkId: number) => OptionItem[];
  isLoading: boolean;
  quickTradeWaitTime?: number;
  error: Error | null;
}

const QUICK_TRADE_PAYMENTS = ["MTN Mobile Money", "Bank Transfer"];
const QUICK_TRADE_NETWORKS: number[] = [base.id, celo.id];

export function useQuickTradeOptions(): QuickTradeOptions {
  const sdk = useHostStore(state => state.sdk);
  const [quickTradeWaitTime, setQuickTradeWaitTime] = React.useState<number | undefined>(
    sdk?.config.quickTradeWaitTime,
  );
  // Fetch all config data

  useEffect(() => {
    if (!sdk) return;
    const unsubscribe = sdk.subscribe("INITIALIZED", e => {
      console.log("Received INITIALIZED event in useQuickTradeOptions", e);
      setQuickTradeWaitTime(e.data.quickTradeWaitTime);
    });
    return unsubscribe;
  }, [sdk]);

  const { currencies, networks, paymentMethods, tokens, isLoading, error } = useP2PConfig();

  // Helper functions to create icon components
  const createNetworkIcon = (networkName: string) =>
    React.createElement("img", {
      src: getNetworkIconUrl(networkName),
      alt: networkName,
      width: 20,
      height: 20,
      className: "rounded-full",
    });

  const createTokenIcon = (tokenSymbol: string) =>
    React.createElement("img", {
      src: getTokenIconUrl(tokenSymbol),
      alt: tokenSymbol,
      width: 20,
      height: 20,
      className: "rounded-full",
    });

  // Transform data into option format
  const networkOptions: OptionItem[] =
    networks
      ?.filter(network =>
        network.status === "active" && sdk?.env === "mainnet" ? QUICK_TRADE_NETWORKS.includes(network.id) : true,
      )
      .map(network => ({
        value: network.id.toString(),
        label: network.name,
        icon: createNetworkIcon(network.name),
      })) || [];

  const paymentOptions: OptionItem[] =
    paymentMethods
      ?.filter(method => (sdk?.env === "mainnet" ? QUICK_TRADE_PAYMENTS.includes(method.name) : true))
      .map(method => ({
        value: method.id,
        label: method.name,
        icon: method.icon,
      })) || [];

  const currencyOptions: OptionItem[] =
    currencies
      ?.filter(currency => currency.status === "active")
      .map(currency => ({
        value: currency.id,
        label: currency.symbol,
        icon: currency.icon || "💰",
      })) || [];

  const tokenOptions: OptionItem[] =
    tokens
      ?.filter(token => token.status === "active")
      .map(token => ({
        value: `${token.address}-${token.networkId}`, // Unique key combining address and network
        label: token.symbol,
        icon: createTokenIcon(token.symbol),
      })) || [];

  // Helper function to get tokens for a specific network
  const getTokensForNetwork = (networkId: number): OptionItem[] => {
    if (!tokens || !networks) return [];
    return tokens
      .filter(token => token.status === "active" && token.networkId === networkId)
      .map(token => ({
        value: `${token.address}-${token.networkId}`,
        label: token.symbol,
        icon: createTokenIcon(token.symbol),
      }));
  };

  return {
    networkOptions,
    paymentOptions,
    currencyOptions,
    tokenOptions,
    getTokensForNetwork,
    quickTradeWaitTime,
    isLoading,
    error,
  };
}
